import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AtlasLanguageService, Language } from '@galaxy/atlas/core';
import { getLocale } from '@vendasta/galaxy/i18n';
import { Observable } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';

export enum LOCALES {
  EN = 'en',
  FR = 'fr',
  FR_FR = 'fr-fr',
  FR_CA = 'fr-ca',
  CS = 'cs',
  PT = 'pt',
  NL = 'nl',
  DE = 'de',
  ES_419 = 'es-419',
  RU = 'ru',
  AF = 'af',
  HE = 'he',
}

@Injectable({ providedIn: 'root' })
export class LanguageService {
  public currentLocale: LOCALES;
  public currentLocale$: Observable<LOCALES>;

  // the starting language is loaded from a previously stored local storage entry
  private readonly startingLanguage: Language;

  constructor(private atlasLanguageService: AtlasLanguageService) {
    this.startingLanguage = this.getEnumValueFromStringValue(getLocale());
    this.currentLocale$ = atlasLanguageService.language$.pipe(
      startWith(this.startingLanguage),
      map((lang: Language) => {
        const locale = this.getLocaleEnumValueFromLanguageEnumValue(lang);
        return locale ? locale : this.getLocaleEnumValueFromLanguageEnumValue(this.startingLanguage);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.currentLocale$.pipe(takeUntilDestroyed()).subscribe((locale: LOCALES) => {
      this.currentLocale = locale;
    });
  }

  private getEnumValueFromStringValue(stringValue: string): Language {
    switch (stringValue) {
      case Language.FRENCH.toString():
      case Language.FRENCH_CANADA.toString():
        return Language.FRENCH_CANADA;
      case Language.FRENCH_FRANCE.toString():
        return Language.FRENCH_FRANCE;
      case Language.CZECH.toString():
        return Language.CZECH;
      case Language.PORTUGUESE.toString():
        return Language.PORTUGUESE;
      case Language.DUTCH.toString():
        return Language.DUTCH;
      case Language.GERMAN.toString():
        return Language.GERMAN;
      case Language.SPANISH_LATIN_AMERICA.toString():
        return Language.SPANISH_LATIN_AMERICA;
      case Language.ENGLISH.toString():
        return Language.ENGLISH;
      case Language.RUSSIAN.toString():
        return Language.RUSSIAN;
      case Language.AFRIKAANS.toString():
        return Language.AFRIKAANS;
      case Language.HEBREW.toString():
        return Language.HEBREW;
      default:
        console.error('unknown locale found, defaulting to English');
        return Language.ENGLISH;
    }
  }

  private getLocaleEnumValueFromLanguageEnumValue(value: Language): LOCALES {
    switch (value) {
      case Language.FRENCH:
      case Language.FRENCH_CANADA:
        return LOCALES.FR_CA;
      case Language.FRENCH_FRANCE:
        return LOCALES.FR_FR;
      case Language.CZECH:
        return LOCALES.CS;
      case Language.PORTUGUESE:
        return LOCALES.PT;
      case Language.DUTCH:
        return LOCALES.NL;
      case Language.GERMAN:
        return LOCALES.DE;
      case Language.SPANISH_LATIN_AMERICA:
        return LOCALES.ES_419;
      case Language.ENGLISH:
        return LOCALES.EN;
      case Language.RUSSIAN:
        return LOCALES.RU;
      case Language.AFRIKAANS:
        return LOCALES.AF;
      case Language.HEBREW:
        return LOCALES.HE;
      default:
        return null;
    }
  }
}
